import type { PropsWithChildren } from "react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { coordinatedSigninSilent } from "authentication/tokenRefreshLock";
import { useSigninRedirect } from "authentication/useSigninRedirect";
import { Spinner } from "common/components";
import {
  getIsAuthenticatingCookie,
  getRefreshTokenCookie,
  setIsAuthenticatingCookie,
  setRefreshTokenCookie,
} from "common/utilities/cookies";
import { readOidcUserFromLocalStorage } from "common/utilities/localStorage";
import { logger } from "common/utilities/logger";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useConst } from "common/utilities/useConst";
import { useGo } from "common/utilities/useGo";
import { unauthenticate } from "domains/authentication/actions";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { useFetchPermissions } from "entities/permissions/useFetchPermissions";

type AuthLoaderProps = PropsWithChildren & { isAuthLoading: boolean };

/**
 * Displays a loading spinner in place of page content while a blocking authentication action is being performed.
 */
export const AuthLoader = ({ children, isAuthLoading }: AuthLoaderProps) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const go = useGo();
  const fetchPermissions = useFetchPermissions();
  const { pathname, search } = useLocation();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const signinRedirect = useSigninRedirect();
  const [checkedIfIsLoggedInInWeforum, setCheckedIfIsLoggedInInWeforum] = useState<boolean>(false);
  const [skipFWSLoginCheck, setSkipFWSLoginCheck] = useState<boolean>(false);

  const removeUser = useConst(() => auth.removeUser.bind(auth));
  const signinSilent = useConst(() => auth.signinSilent.bind(auth));
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated);

  useEffect(() => {
    /*
    check refresh token.
      if we have one, silent sign in and done
      if not
        check remote endpoint
          if authenticated, redirect login AND set the refresh token in the cookie, then done
          it not
            check local user
              if we have one, delete it and log the user out => we should not be logged in
              if not
                end flow
     */

    const ssoHandler = async () => {
      const { pathname, search } = location;
      const searchParams = new URLSearchParams(search);
      // do not autologin if you are coming from app-redirect! This is to prevent logging in when the auth session is intended for the app
      if (pathname === "/app-redirect") {
        setSkipFWSLoginCheck(true);
        return;
      }

      const isComingFromPaymentPortal = document.referrer.includes(
        new URL(import.meta.env.VITE_DIGITAL_MEMBERS_URL).hostname,
      );
      // you arrived from the payment portal and are logged in, so we remove the local user and a login by refreshing
      if (isComingFromPaymentPortal && isAuthenticatedRef.current) {
        removeUser().catch((error: unknown) =>
          logger.errorLocal(`[AuthLoader first effect] Failed to remove user`, error),
        );
        location.reload();
      }
      // get variables that we need later on
      const destination = searchParams.get("destination");
      const localOidcUser = readOidcUserFromLocalStorage();
      const localStoreRefreshToken = localOidcUser?.refresh_token;
      const cookieRefreshToken = getRefreshTokenCookie();

      // if the cookie is there, log in and cancel the rest of the flow
      if (cookieRefreshToken) {
        // there is some inconsistency, so get rid of whatever we have locally
        if (cookieRefreshToken !== localStoreRefreshToken) {
          // we might need to call with true here
          removeUser().catch((error: unknown) =>
            logger.errorLocal("[AuthLoader first effect] Failed to remove user", error),
          );
        }
        // the remove user method calls the WithRemoteRefreshTokenStorage remove method, which deletes the cookie - we need to put it back, because for this flow it should not be removed
        // if its removed, the signinSilent will not sign in (as there is no refresh token anymore)
        setRefreshTokenCookie(cookieRefreshToken);
        await coordinatedSigninSilent({
          fetchPermissions,
          getCurrentOidcUser: readOidcUserFromLocalStorage,
          knownIdToken: localOidcUser?.id_token,
          knownRefreshToken: localStoreRefreshToken,
          signinSilent,
        }).catch((error: unknown) => logger.errorLocal("[AuthLoader first effect] Failed to sign in silently", error));

        setCheckedIfIsLoggedInInWeforum(true);
        setIsAuthenticatingCookie(false);
        return;
      }

      // if we are currently not doing a login (see docs/ADR/IS_AUTHENTICATING.md)
      if (getIsAuthenticatingCookie() !== "true") {
        const isRemotelyLoggedIn = await fetch(`${import.meta.env.VITE_LOGIN_URL}/ssologin/login`, {
          cache: "no-cache",
          credentials: "include",
        })
          .then((response) => response.json())
          .then((body: { isauthenticated: boolean }) => body.isauthenticated);
        if (isRemotelyLoggedIn) {
          signinRedirect();
          return;
        }
        if (localStoreRefreshToken) {
          dispatch(unauthenticate({ isUserLogout: true }));
          removeUser().catch((error: unknown) =>
            logger.errorLocal("[AuthLoader first effect] Failed to remove user", error),
          );
          setCheckedIfIsLoggedInInWeforum(true);
          setIsAuthenticatingCookie(false);
        }
      }

      setCheckedIfIsLoggedInInWeforum(true);
      if (destination) {
        go(destination);
      }
    };

    void ssoHandler();
  }, [dispatch, fetchPermissions, go, removeUser, signinRedirect, signinSilent]);

  // If the user is on the registration flow, then activate the login modal
  useEffect(() => {
    const { pathname, search } = location;
    const searchParams = new URLSearchParams(search);
    const code = searchParams.get("code");
    const original_state = searchParams.get("original_state");
    // If this condition holds, then the user is on the registration flow
    if (pathname !== "/app-redirect" && original_state !== null && code === null) dispatch(triggerLogin());
  }, [dispatch]);

  // If the user is authenticated but still 'on' an OIDC auth flow, then remove the 'code' and 'state' search
  // parameters to allow them to progress
  const searchParams = new URLSearchParams(search);
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const isOidcAuthFlow = pathname !== "/app-redirect" && code !== null && state !== null;

  useEffect(() => {
    if (!isAuthenticated || !isOidcAuthFlow) return;
    const { pathname, search } = location;
    const searchParams = new URLSearchParams(search);
    searchParams.delete("code");
    searchParams.delete("state");
    go(`${pathname}?${searchParams.toString()}`, { replace: true });
  }, [go, isAuthenticated, isOidcAuthFlow]);

  return !skipFWSLoginCheck && (!checkedIfIsLoggedInInWeforum || isAuthLoading || isOidcAuthFlow) ? (
    <CentreSpinner />
  ) : (
    children
  );
};

const CentreSpinner = styled(Spinner)`
  color: ${({ theme }) => theme.colors.textBright};
  height: 4.6875rem;
  inset: calc(50% - (4.6875rem / 2));
  position: absolute;
  width: 4.6875rem;
`;
